import { useState, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { getImageUrl } from '../../utils/imageUtils';
import { formatError } from '../../utils/errorUtils';
import {
    FileText,
    Upload,
    Loader2,
    Trash2,
    Plus,
    X,
    Lock,
    Clock,
    Mail,
    Phone,
    Sparkles,
    ExternalLink,
    Image as ImageIcon,
    Eye
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AboutPageEditor() {
    const { agency, updateAgencyProfile, getAuthHeader } = useAuth();
    const [loading, setLoading] = useState(false);
    const [uploadingImage, setUploadingImage] = useState(false);
    const imageInputRef = useRef(null);
    const [newSpec, setNewSpec] = useState('');

    const isPremium = agency?.subscription === 'premium' || agency?.subscription === 'enterprise';

    const [formData, setFormData] = useState({
        about: agency?.about || '',
        tagline: agency?.tagline || '',
        about_image: agency?.about_image || '',
        business_hours: agency?.business_hours || '',
        business_email: agency?.business_email || '',
        business_phone: agency?.business_phone || '',
        specializations: agency?.specializations || [],
    });

    const updateField = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
        if (!validTypes.includes(file.type)) {
            toast.error('Please upload a JPG, PNG, or WebP image');
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            toast.error('Image must be under 5MB');
            return;
        }

        setUploadingImage(true);
        try {
            const form = new FormData();
            form.append('file', file);
            const res = await axios.post(`${API_URL}/api/upload`, form, {
                headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
            });
            updateField('about_image', res.data.url);
            toast.success('Image uploaded!');
        } catch (error) {
            const message = formatError(error.response?.data?.detail) || error.message;
            toast.error('Upload failed: ' + message);
        } finally {
            setUploadingImage(false);
            if (imageInputRef.current) imageInputRef.current.value = '';
        }
    };

    const removeImage = () => {
        updateField('about_image', '');
        toast.info('Image removed. Save to apply.');
    };

    const addSpecialization = () => {
        const trimmed = newSpec.trim();
        if (!trimmed) return;
        if (formData.specializations.includes(trimmed)) {
            toast.error('Already added');
            return;
        }
        if (formData.specializations.length >= 10) {
            toast.error('Maximum 10 specializations');
            return;
        }
        updateField('specializations', [...formData.specializations, trimmed]);
        setNewSpec('');
    };

    const removeSpecialization = (index) => {
        updateField('specializations', formData.specializations.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const submitData = { ...formData };
            // Strip premium fields for free users
            if (!isPremium) {
                delete submitData.business_hours;
                delete submitData.business_email;
                delete submitData.business_phone;
                delete submitData.specializations;
            }
            await updateAgencyProfile(submitData);
            toast.success('About page updated!');
        } catch (error) {
            const message = formatError(error.response?.data?.detail) || 'Failed to update';
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-6 lg:p-8 max-w-4xl" data-testid="about-editor-page">
            {/* Header */}
            <div className="mb-6 flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-heading font-bold text-navy-900">About Page Editor</h1>
                    <p className="text-navy-600">Customize how your agency appears on the public About page</p>
                </div>
                <a
                    href={`/agency/${agency?.slug}/about`}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <Button variant="outline" className="gap-2" data-testid="preview-about-btn">
                        <Eye className="w-4 h-4" />
                        Preview
                    </Button>
                </a>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* About Content */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                            <FileText className="w-5 h-5 text-sky-500" />
                            About Content
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="tagline">Tagline / Motto</Label>
                            <Input
                                id="tagline"
                                placeholder="e.g., Redefining luxury real estate since 2005"
                                value={formData.tagline}
                                onChange={(e) => updateField('tagline', e.target.value)}
                                maxLength={150}
                                data-testid="tagline-input"
                            />
                            <p className="text-xs text-navy-400">{formData.tagline.length}/150 characters</p>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="about">About Your Agency</Label>
                            <Textarea
                                id="about"
                                placeholder="Tell visitors about your agency, your mission, experience, and what makes you unique..."
                                value={formData.about}
                                onChange={(e) => updateField('about', e.target.value)}
                                className="min-h-[180px]"
                                data-testid="about-text-input"
                            />
                            <p className="text-xs text-navy-400">
                                This appears prominently on your About page. Write in paragraphs for best results.
                            </p>
                        </div>
                    </CardContent>
                </Card>

                {/* Hero Image */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                            <ImageIcon className="w-5 h-5 text-sky-500" />
                            About Page Image
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <p className="text-sm text-navy-500">
                            Upload a image for your About page hero section. This could be your team, office, or a property showcase.
                        </p>

                        {formData.about_image ? (
                            <div className="relative rounded-xl overflow-hidden border border-navy-100 group max-w-lg">
                                <img
                                    src={getImageUrl(formData.about_image)}
                                    alt="About hero"
                                    className="w-full h-56 object-cover"
                                />
                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="secondary"
                                        onClick={() => imageInputRef.current?.click()}
                                        className="gap-2"
                                    >
                                        <Upload className="w-4 h-4" /> Replace
                                    </Button>
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="destructive"
                                        onClick={removeImage}
                                        className="gap-2"
                                    >
                                        <Trash2 className="w-4 h-4" /> Remove
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <button
                                type="button"
                                onClick={() => imageInputRef.current?.click()}
                                className="w-full max-w-lg h-48 rounded-xl border-2 border-dashed border-navy-200 flex flex-col items-center justify-center text-navy-400 hover:bg-navy-50 hover:border-sky-300 transition-all cursor-pointer"
                                disabled={uploadingImage}
                            >
                                {uploadingImage ? (
                                    <Loader2 className="w-8 h-8 animate-spin mb-2" />
                                ) : (
                                    <>
                                        <Upload className="w-8 h-8 mb-2" />
                                        <span className="font-medium">Upload Image</span>
                                        <span className="text-xs mt-1">JPG, PNG or WebP — Max 5MB</span>
                                    </>
                                )}
                            </button>
                        )}

                        <input
                            ref={imageInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            onChange={handleImageUpload}
                            className="hidden"
                        />
                    </CardContent>
                </Card>

                {/* Business Information (Premium) */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Clock className="w-5 h-5 text-sky-500" />
                            Business Information
                            {!isPremium && (
                                <span className="flex items-center gap-1 text-xs font-normal bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                                    <Lock className="w-3 h-3" />
                                    Premium
                                </span>
                            )}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {!isPremium && (
                            <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 mb-2">
                                <p className="text-xs text-amber-700">
                                    Upgrade to Premium to show business hours, contact info, and specializations on your About page.
                                </p>
                            </div>
                        )}

                        <div className="grid md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="business_hours" className="flex items-center gap-2">
                                    <Clock className="w-4 h-4 text-navy-400" />
                                    Business Hours
                                </Label>
                                <Input
                                    id="business_hours"
                                    placeholder="e.g., Mon-Sat 9:00 AM - 7:00 PM"
                                    value={formData.business_hours}
                                    onChange={(e) => updateField('business_hours', e.target.value)}
                                    disabled={!isPremium}
                                    className={!isPremium ? 'opacity-50 cursor-not-allowed' : ''}
                                    data-testid="business-hours-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="business_email" className="flex items-center gap-2">
                                    <Mail className="w-4 h-4 text-navy-400" />
                                    Public Email
                                </Label>
                                <Input
                                    id="business_email"
                                    type="email"
                                    placeholder="contact@youragency.com"
                                    value={formData.business_email}
                                    onChange={(e) => updateField('business_email', e.target.value)}
                                    disabled={!isPremium}
                                    className={!isPremium ? 'opacity-50 cursor-not-allowed' : ''}
                                    data-testid="business-email-input"
                                />
                            </div>
                            <div className="space-y-2 md:col-span-2">
                                <Label htmlFor="business_phone" className="flex items-center gap-2">
                                    <Phone className="w-4 h-4 text-navy-400" />
                                    Public Phone
                                </Label>
                                <Input
                                    id="business_phone"
                                    placeholder="+91 98765 43210"
                                    value={formData.business_phone}
                                    onChange={(e) => updateField('business_phone', e.target.value)}
                                    disabled={!isPremium}
                                    className={!isPremium ? 'opacity-50 cursor-not-allowed' : ''}
                                    data-testid="business-phone-input"
                                />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Specializations (Premium) */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Sparkles className="w-5 h-5 text-sky-500" />
                            Specializations
                            {!isPremium && (
                                <span className="flex items-center gap-1 text-xs font-normal bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                                    <Lock className="w-3 h-3" />
                                    Premium
                                </span>
                            )}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <p className="text-sm text-navy-500">
                            Add specializations to showcase your expertise. These appear as badges on your About page.
                        </p>

                        {formData.specializations.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                                {formData.specializations.map((spec, index) => (
                                    <Badge
                                        key={index}
                                        variant="secondary"
                                        className="bg-sky-50 text-sky-700 border-sky-200 px-3 py-1.5 text-sm gap-2"
                                    >
                                        {spec}
                                        {isPremium && (
                                            <button
                                                type="button"
                                                onClick={() => removeSpecialization(index)}
                                                className="hover:text-red-500 transition-colors"
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        )}
                                    </Badge>
                                ))}
                            </div>
                        )}

                        {isPremium && (
                            <div className="flex gap-2">
                                <Input
                                    placeholder="e.g., Luxury Villas, Commercial Properties..."
                                    value={newSpec}
                                    onChange={(e) => setNewSpec(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            addSpecialization();
                                        }
                                    }}
                                    data-testid="specialization-input"
                                />
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={addSpecialization}
                                    className="gap-2 shrink-0"
                                >
                                    <Plus className="w-4 h-4" />
                                    Add
                                </Button>
                            </div>
                        )}

                        {!isPremium && (
                            <div className="bg-amber-50 border border-amber-100 rounded-lg p-3">
                                <p className="text-xs text-amber-700">
                                    Upgrade to Premium to add specialization tags.
                                </p>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Save */}
                <Button
                    type="submit"
                    className="w-full bg-navy-900 hover:bg-navy-800 h-11"
                    disabled={loading}
                    data-testid="save-about-btn"
                >
                    {loading ? (
                        <>
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            Saving...
                        </>
                    ) : (
                        'Save About Page'
                    )}
                </Button>
            </form>
        </div>
    );
}
